import { useState } from "react";
import { Phone, Mail, MapPin, Facebook, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Reveal, SectionHeading } from "@/components/site/Reveal";

const CATEGORIES = [
  "Mini Category (Ages 4–7)",
  "Little Category (Ages 8–10)",
  "Pre-Teen (Ages 11–14)",
  "Teen (Ages 15–18)",
  "Miss Top Universe (Ages 19–27)",
  "Mr Top Universe (Ages 19–27)",
  "Mrs Category (Ages 29–40)",
  "Grooming & Training Academy",
  "Other Enquiry",
];

const inputCls =
  "w-full border border-white/15 bg-black/40 px-5 py-4 text-sm font-light tracking-wide text-white placeholder:text-neutral-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]";

export default function Contact({ settings }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: CATEGORIES[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Your enquiry has been received. The house of D'La Valentina will reach out soon.");
      setForm({ name: "", email: "", phone: "", category: CATEGORIES[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <SectionHeading kicker="Begin Your Reign" title="Take Your Place on the Stage" testid="contact-heading" />
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <p className="max-w-md text-sm font-light leading-loose tracking-wide text-neutral-300 md:text-base">
              Whether you dream of your child's first crown or your own triumphant return to the spotlight —
              write to us. Every reign begins with a single step onto the stage.
            </p>
            <a
              href="/register"
              data-testid="contact-register-link"
              className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37] transition-all duration-300 hover:gap-4"
            >
              Applying for a pageant? Use the full registration form →
            </a>
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-5">
                <Phone className="mt-1 h-5 w-5 text-[#D4AF37]" strokeWidth={1} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Call Us</div>
                  <a data-testid="contact-phone-1" href={`tel:${(settings.contact_phone1 || "").replace(/\s/g, "")}`} className="mt-1 block text-sm text-neutral-200 transition-colors hover:text-[#D4AF37]">
                    {settings.contact_phone1}
                  </a>
                  <a data-testid="contact-phone-2" href={`tel:${(settings.contact_phone2 || "").replace(/\s/g, "")}`} className="block text-sm text-neutral-200 transition-colors hover:text-[#D4AF37]">
                    {settings.contact_phone2}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <Mail className="mt-1 h-5 w-5 text-[#D4AF37]" strokeWidth={1} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Write to Us</div>
                  <a data-testid="contact-email" href={`mailto:${settings.contact_email}`} className="mt-1 block text-sm text-neutral-200 transition-colors hover:text-[#D4AF37]">
                    {settings.contact_email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <MapPin className="mt-1 h-5 w-5 text-[#D4AF37]" strokeWidth={1} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">The House</div>
                  <span data-testid="contact-address" className="mt-1 block text-sm text-neutral-200">
                    {settings.contact_address}
                  </span>
                </div>
              </div>
              {settings.facebook_url && (
                <div className="flex items-start gap-5">
                  <Facebook className="mt-1 h-5 w-5 text-[#D4AF37]" strokeWidth={1} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Follow the Reign</div>
                    <a
                      data-testid="contact-facebook-link"
                      href={settings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block text-sm text-neutral-200 transition-colors hover:text-[#D4AF37]"
                    >
                      D'La Valentina on Facebook
                    </a>
                  </div>
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-5 border border-[#D4AF37]/20 bg-black/30 p-8 md:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your Name" className={inputCls} />
                <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email Address" className={inputCls} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone Number" className={inputCls} />
                <select data-testid="contact-category-select" value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-black">{c}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your dream..." className={`${inputCls} resize-none`} />
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-all duration-300 hover:bg-[#F9F1CC] disabled:opacity-50"
              >
                <Crown className="h-4 w-4" strokeWidth={1.5} />
                {sending ? "Sending..." : "Claim Your Crown"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
